/**
 * Normative data utilities for percentile calculations
 * WHO growth charts and adult reference ranges
 */

import { Gender } from '../types/patient'

/**
 * Calculate age in years from date of birth
 */
export function calculateAge(dateOfBirth: string): number {
  const birthDate = new Date(dateOfBirth)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

/**
 * Calculate age in months from date of birth
 */
export function calculateAgeInMonths(dateOfBirth: string): number {
  const birthDate = new Date(dateOfBirth)
  const today = new Date()
  const months = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth())
  return months
}

/**
 * WHO Growth Chart Data - Height for Age (Boys)
 * Percentiles: 3rd, 5th, 10th, 25th, 50th, 75th, 90th, 95th, 97th
 * Ages in months: 0-60 months (0-5 years)
 * Data points for key ages (simplified - in production, use full WHO tables)
 */
const WHO_HEIGHT_BOYS: { [ageMonths: number]: number[] } = {
  // Age in months: [P3, P5, P10, P25, P50, P75, P90, P95, P97] in cm
  0: [46.1, 46.8, 47.9, 49.4, 50.8, 52.3, 53.7, 54.7, 55.6],
  6: [63.3, 64.5, 65.9, 67.6, 69.2, 70.9, 72.5, 73.7, 74.5],
  12: [71.0, 72.1, 73.5, 75.3, 77.0, 78.7, 80.3, 81.4, 82.2],
  18: [76.9, 78.0, 79.4, 81.2, 82.9, 84.6, 86.2, 87.3, 88.1],
  24: [81.7, 82.8, 84.2, 86.0, 87.8, 89.5, 91.2, 92.3, 93.1],
  36: [88.7, 89.8, 91.2, 93.1, 95.0, 96.9, 98.7, 99.9, 100.8],
  48: [94.9, 96.0, 97.5, 99.5, 101.6, 103.6, 105.6, 106.8, 107.8],
  60: [100.7, 101.8, 103.3, 105.4, 107.6, 109.8, 111.9, 113.1, 114.2],
}

/**
 * WHO Growth Chart Data - Height for Age (Girls)
 */
const WHO_HEIGHT_GIRLS: { [ageMonths: number]: number[] } = {
  0: [45.4, 46.1, 47.2, 48.7, 50.1, 51.5, 52.9, 53.9, 54.7],
  6: [61.2, 62.4, 63.8, 65.5, 67.1, 68.7, 70.3, 71.4, 72.2],
  12: [69.2, 70.3, 71.7, 73.4, 75.1, 76.8, 78.4, 79.5, 80.3],
  18: [74.9, 76.0, 77.4, 79.2, 80.9, 82.6, 84.2, 85.3, 86.1],
  24: [80.0, 81.1, 82.5, 84.3, 86.1, 87.8, 89.5, 90.6, 91.4],
  36: [87.4, 88.5, 90.0, 91.9, 93.9, 95.8, 97.7, 98.9, 99.8],
  48: [93.9, 95.0, 96.5, 98.5, 100.6, 102.7, 104.7, 105.9, 106.9],
  60: [99.9, 101.0, 102.5, 104.6, 106.9, 109.1, 111.2, 112.4, 113.4],
}

/**
 * WHO Growth Chart Data - Weight for Age (Boys)
 */
const WHO_WEIGHT_BOYS: { [ageMonths: number]: number[] } = {
  0: [2.5, 2.6, 2.7, 2.9, 3.1, 3.4, 3.7, 3.9, 4.1],
  6: [6.4, 6.7, 7.0, 7.5, 8.0, 8.6, 9.2, 9.6, 10.0],
  12: [8.4, 8.7, 9.1, 9.7, 10.3, 11.0, 11.7, 12.2, 12.6],
  18: [9.8, 10.1, 10.6, 11.3, 12.0, 12.8, 13.6, 14.1, 14.5],
  24: [10.8, 11.2, 11.7, 12.4, 13.1, 14.0, 14.9, 15.4, 15.9],
  36: [12.7, 13.1, 13.7, 14.6, 15.5, 16.5, 17.6, 18.2, 18.7],
  48: [14.6, 15.1, 15.7, 16.7, 17.8, 19.0, 20.2, 20.9, 21.5],
  60: [16.3, 16.8, 17.5, 18.6, 19.9, 21.2, 22.6, 23.4, 24.1],
}

/**
 * WHO Growth Chart Data - Weight for Age (Girls)
 */
const WHO_WEIGHT_GIRLS: { [ageMonths: number]: number[] } = {
  0: [2.4, 2.5, 2.6, 2.8, 3.0, 3.2, 3.5, 3.7, 3.9],
  6: [5.8, 6.1, 6.4, 6.9, 7.4, 8.0, 8.6, 9.0, 9.4],
  12: [7.8, 8.1, 8.5, 9.1, 9.7, 10.4, 11.1, 11.6, 12.0],
  18: [9.1, 9.5, 9.9, 10.6, 11.3, 12.1, 12.9, 13.4, 13.8],
  24: [10.2, 10.5, 11.0, 11.7, 12.4, 13.2, 14.1, 14.6, 15.1],
  36: [12.1, 12.5, 13.1, 14.0, 14.9, 15.9, 17.0, 17.6, 18.1],
  48: [13.9, 14.3, 14.9, 15.9, 17.0, 18.2, 19.4, 20.0, 20.6],
  60: [15.3, 15.8, 16.4, 17.5, 18.8, 20.1, 21.5, 22.2, 22.9],
}

/**
 * WHO BMI for Age (Boys) - simplified data points
 */
const WHO_BMI_BOYS: { [ageMonths: number]: number[] } = {
  0: [11.0, 11.2, 11.5, 12.0, 12.5, 13.1, 13.7, 14.1, 14.5],
  12: [14.2, 14.5, 14.9, 15.5, 16.1, 16.8, 17.5, 18.0, 18.3],
  24: [14.8, 15.1, 15.5, 16.1, 16.7, 17.4, 18.1, 18.6, 19.0],
  36: [14.5, 14.8, 15.2, 15.8, 16.5, 17.2, 18.0, 18.5, 18.9],
  48: [14.3, 14.6, 15.0, 15.6, 16.3, 17.1, 17.9, 18.4, 18.8],
  60: [14.2, 14.5, 14.9, 15.5, 16.2, 17.0, 17.8, 18.3, 18.7],
}

/**
 * WHO BMI for Age (Girls) - simplified data points
 */
const WHO_BMI_GIRLS: { [ageMonths: number]: number[] } = {
  0: [10.8, 11.0, 11.3, 11.8, 12.3, 12.9, 13.5, 13.9, 14.3],
  12: [13.8, 14.1, 14.5, 15.1, 15.7, 16.4, 17.1, 17.6, 18.0],
  24: [14.4, 14.7, 15.1, 15.7, 16.3, 17.0, 17.7, 18.2, 18.6],
  36: [14.1, 14.4, 14.8, 15.4, 16.1, 16.8, 17.6, 18.1, 18.5],
  48: [13.9, 14.2, 14.6, 15.2, 15.9, 16.7, 17.5, 18.0, 18.4],
  60: [13.8, 14.1, 14.5, 15.1, 15.8, 16.6, 17.4, 17.9, 18.3],
}

/**
 * Interpolate percentile value between two data points
 */
function interpolatePercentile(
  ageMonths: number,
  data: { [ageMonths: number]: number[] },
  percentileIndex: number,
): number | null {
  const ages = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b)

  if (ageMonths < ages[0] || ageMonths > ages[ages.length - 1]) {
    return null
  }

  // Find surrounding ages
  let lowerAge = ages[0]
  let upperAge = ages[ages.length - 1]

  for (let i = 0; i < ages.length - 1; i++) {
    if (ageMonths >= ages[i] && ageMonths <= ages[i + 1]) {
      lowerAge = ages[i]
      upperAge = ages[i + 1]
      break
    }
  }

  const lowerValue = data[lowerAge][percentileIndex]
  const upperValue = data[upperAge][percentileIndex]

  if (lowerAge === upperAge) {
    return lowerValue
  }

  // Linear interpolation
  const ratio = (ageMonths - lowerAge) / (upperAge - lowerAge)
  return lowerValue + (upperValue - lowerValue) * ratio
}

/**
 * Calculate percentile for a value given reference data
 */
function calculatePercentile(
  value: number,
  ageMonths: number,
  data: { [ageMonths: number]: number[] },
): number {
  const percentileValues = [3, 5, 10, 25, 50, 75, 90, 95, 97]
  Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b)

  // Get interpolated values for each percentile
  const values: number[] = []
  for (let i = 0; i < percentileValues.length; i++) {
    const interpolated = interpolatePercentile(ageMonths, data, i)
    if (interpolated !== null) {
      values.push(interpolated)
    }
  }

  if (values.length === 0) return 50 // Default to 50th if no data

  // Find which percentile range the value falls into
  if (value < values[0]) return 3
  if (value > values[values.length - 1]) return 97

  for (let i = 0; i < values.length - 1; i++) {
    if (value >= values[i] && value <= values[i + 1]) {
      // Linear interpolation between percentiles
      const ratio = (value - values[i]) / (values[i + 1] - values[i])
      const lowerPct = percentileValues[i]
      const upperPct = percentileValues[i + 1]
      return Math.round(lowerPct + (upperPct - lowerPct) * ratio)
    }
  }

  return 50
}

/**
 * Calculate height percentile for a child
 */
export function calculateHeightPercentile(
  heightCm: number,
  dateOfBirth: string,
  gender: Gender,
): number | null {
  const ageMonths = calculateAgeInMonths(dateOfBirth)
  if (ageMonths < 0 || ageMonths > 60) return null // Only for children 0-5 years

  const data = gender === 'male' ? WHO_HEIGHT_BOYS : WHO_HEIGHT_GIRLS
  return calculatePercentile(heightCm, ageMonths, data)
}

/**
 * Calculate weight percentile for a child
 */
export function calculateWeightPercentile(
  weightKg: number,
  dateOfBirth: string,
  gender: Gender,
): number | null {
  const ageMonths = calculateAgeInMonths(dateOfBirth)
  if (ageMonths < 0 || ageMonths > 60) return null

  const data = gender === 'male' ? WHO_WEIGHT_BOYS : WHO_WEIGHT_GIRLS
  return calculatePercentile(weightKg, ageMonths, data)
}

/**
 * Calculate BMI percentile for a child
 */
export function calculateBMIPercentile(bmi: number, dateOfBirth: string, gender: Gender): number | null {
  const ageMonths = calculateAgeInMonths(dateOfBirth)
  if (ageMonths < 0 || ageMonths > 60) return null

  const data = gender === 'male' ? WHO_BMI_BOYS : WHO_BMI_GIRLS
  return calculatePercentile(bmi, ageMonths, data)
}

/**
 * Adult BMI categories
 */
export function getBMICategory(bmi: number): 'underweight' | 'normal' | 'overweight' | 'obese' {
  if (bmi < 18.5) return 'underweight'
  if (bmi < 25) return 'normal'
  if (bmi < 30) return 'overweight'
  return 'obese'
}

/**
 * Adult reference ranges for vital signs
 */
export const ADULT_REFERENCE_RANGES = {
  bloodPressure: {
    systolic: { min: 90, max: 120, optimal: 120 },
    diastolic: { min: 60, max: 80, optimal: 80 },
  },
  heartRate: { min: 60, max: 100, optimal: 72 },
  respiratoryRate: { min: 12, max: 20, optimal: 16 },
  temperature: { min: 36.1, max: 37.2, optimal: 36.5 }, // Celsius
  oxygenSaturation: { min: 95, max: 100, optimal: 98 }, // %
  cholesterol: { min: 0, max: 5.2, optimal: 4.0 }, // mmol/L
  ldl: { min: 0, max: 3.0, optimal: 2.0 }, // mmol/L
  hdl: { min: 1.0, max: 10, optimal: 1.5 }, // mmol/L (higher is better)
  bmi: { min: 18.5, max: 24.9, optimal: 22 },
}

/**
 * Check if a vital value is within normal range (for adults)
 */
export function isWithinNormalRange(
  value: number,
  vitalType: keyof typeof ADULT_REFERENCE_RANGES,
): boolean {
  const range = ADULT_REFERENCE_RANGES[vitalType]
  if (!range || typeof range === 'object' && 'min' in range && 'max' in range) {
    const r = range as { min: number; max: number }
    return value >= r.min && value <= r.max
  }
  return true // Default to normal if range not defined
}
